import React, { Fragment, useState, useMemo, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Building,
  Building2,
  Users,
  Sparkles,
  AlertTriangle,
  Search,
  MoreVertical,
  RefreshCw,
  Check,
  Upload,
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Label } from '../../components/ui/Label'
import { Card, CardContent } from '../../components/ui/Card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/Dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/DropdownMenu'
import { OrgTypeSelector } from '../../components/admin/OrgTypeSelector'
import { LogoDropzone } from '../../components/admin/LogoDropzone'
import { adminApi } from '../../services/api/admin'
import { brandingApi } from '../../services/api/branding'
import type { AdminOrgFull, OrgFilterType, OrgType } from '../../types/branding'
import { cn } from '../../lib/utils'
import toast from 'react-hot-toast'

const FILTER_LABELS: Record<OrgFilterType, string> = {
  all: 'All',
  customers: 'Customers',
  demos: 'Demos',
}

const DETERMINISTIC_COLORS = [
  'bg-primary/20 text-primary',
  'bg-violet-500/20 text-violet-600 dark:text-violet-400',
  'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400',
  'bg-amber-500/20 text-amber-600 dark:text-amber-400',
  'bg-rose-500/20 text-rose-600 dark:text-rose-400',
  'bg-sky-500/20 text-sky-600 dark:text-sky-400',
]

function deterministicColorClass(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) & 0xffffffff
  }
  return DETERMINISTIC_COLORS[Math.abs(hash) % DETERMINISTIC_COLORS.length]
}

const ORG_TYPE_LABELS: Record<string, string> = {
  qi: 'Qualified Intermediary',
  bank: 'Bank / Lender',
  brokerage: 'Real Estate Brokerage',
  accounting_firm: 'Accounting Firm',
  law_firm: 'Law Firm',
  enterprise: 'Enterprise',
  platform: 'Platform',
}

const wizardSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(120, 'Name too long'),
  slug: z.string()
    .min(1, 'Slug is required')
    .max(64, 'Slug must be 64 characters or fewer')
    .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase letters, digits, and hyphens only'),
  orgType: z.string().nullable().optional(),
  primaryColor: z.string().optional(),
  accentColor: z.string().optional(),
  website: z.string().optional(),
  demoTenant: z.boolean(),
  logoFile: z.instanceof(File).optional(),
})

type WizardFormValues = z.infer<typeof wizardSchema>

const WIZARD_STEPS = [
  { label: 'Basics', step: 1 },
  { label: 'Branding', step: 2 },
  { label: 'Logo', step: 3 },
  { label: 'Type', step: 4 },
  { label: 'Confirm', step: 5 },
] as const

type WizardStep = 1 | 2 | 3 | 4 | 5

interface WizardProgressProps {
  current: WizardStep
}

const WizardProgress: React.FC<WizardProgressProps> = ({ current }) => (
  <div className="flex items-center justify-center gap-1 mb-6" aria-label="Wizard progress">
    {WIZARD_STEPS.map(({ label, step }) => {
      const complete = step < current
      const active = step === current
      return (
        <Fragment key={step}>
          <div className="flex flex-col items-center gap-1">
            <div
              className={cn(
                'h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-all',
                complete && 'bg-primary border-primary text-primary-foreground',
                active && 'border-primary text-primary bg-primary/10',
                !complete && !active && 'border-border text-muted-foreground',
              )}
              aria-current={active ? 'step' : undefined}
            >
              {complete ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : step}
            </div>
            <span className={cn(
              'text-xs font-medium',
              active ? 'text-primary' : 'text-muted-foreground',
            )}>
              {label}
            </span>
          </div>
          {step < 5 && (
            <div className={cn(
              'h-0.5 w-6 mt-[-14px] rounded-full transition-colors',
              complete ? 'bg-primary' : 'bg-border',
            )} />
          )}
        </Fragment>
      )
    })}
  </div>
)

interface Step1Props {
  register: ReturnType<typeof useForm<WizardFormValues>>['register']
  control: ReturnType<typeof useForm<WizardFormValues>>['control']
  errors: ReturnType<typeof useForm<WizardFormValues>>['formState']['errors']
}

const WizardStep1: React.FC<Step1Props> = ({ register, control, errors }) => (
  <div className="space-y-5">
    <div className="space-y-1.5">
      <Label htmlFor="wizard-name">Organization name</Label>
      <Input
        id="wizard-name"
        placeholder="Acme Title Company"
        aria-invalid={!!errors.name}
        aria-describedby={errors.name ? 'wizard-name-error' : undefined}
        {...register('name')}
      />
      {errors.name && (
        <p id="wizard-name-error" role="alert" className="text-xs text-destructive">
          {errors.name.message}
        </p>
      )}
    </div>
    <div className="space-y-1.5">
      <Label htmlFor="wizard-slug">URL slug</Label>
      <Input
        id="wizard-slug"
        placeholder="acme-title"
        aria-invalid={!!errors.slug}
        aria-describedby={errors.slug ? 'wizard-slug-error' : undefined}
        {...register('slug')}
      />
      {errors.slug && (
        <p id="wizard-slug-error" role="alert" className="text-xs text-destructive">
          {errors.slug.message}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Used in the portal URL: exchange.open1031.ai/<span className="font-mono">slug</span>
      </p>
    </div>
    <div className="space-y-1.5">
      <Label>Organization type</Label>
      <Controller
        name="orgType"
        control={control}
        render={({ field }) => (
          <OrgTypeSelector
            value={field.value as OrgType | null}
            onChange={(v) => field.onChange(v)}
          />
        )}
      />
    </div>
  </div>
)

interface Step2Props {
  register: ReturnType<typeof useForm<WizardFormValues>>['register']
}

const WizardStep2: React.FC<Step2Props> = ({ register }) => (
  <div className="space-y-5">
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label htmlFor="wizard-primary">Primary color</Label>
        <Input
          id="wizard-primary"
          placeholder="#1a2b3c"
          className="font-mono"
          aria-label="Primary color"
          {...register('primaryColor')}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="wizard-accent">Accent color</Label>
        <Input
          id="wizard-accent"
          placeholder="#4a5b6c"
          className="font-mono"
          aria-label="Accent color"
          {...register('accentColor')}
        />
      </div>
    </div>
    <div className="space-y-1.5">
      <Label htmlFor="wizard-website">Custom domain (optional)</Label>
      <Input
        id="wizard-website"
        placeholder="https://exchange.acmetitle.com"
        {...register('website')}
      />
    </div>
  </div>
)

interface Step3Props {
  control: ReturnType<typeof useForm<WizardFormValues>>['control']
}

const WizardStep3: React.FC<Step3Props> = ({ control }) => (
  <div className="space-y-4">
    <p className="text-sm text-muted-foreground">
      Upload a logo now or add one later from the Branding tab.
    </p>
    <Controller
      name="logoFile"
      control={control}
      render={({ field }) => (
        <div className="space-y-1.5">
          <Label>Logo (light mode)</Label>
          <LogoDropzone
            value={field.value}
            onChange={field.onChange}
          />
        </div>
      )}
    />
    <p className="text-xs text-muted-foreground flex items-center gap-1">
      <Upload className="h-3 w-3 shrink-0" aria-hidden="true" />
      Logo upload is optional — skip to create the org without one.
    </p>
  </div>
)

interface Step4Props {
  control: ReturnType<typeof useForm<WizardFormValues>>['control']
  watch: ReturnType<typeof useForm<WizardFormValues>>['watch']
}

const WizardStep4: React.FC<Step4Props> = ({ control, watch }) => {
  const demoTenant = watch('demoTenant')
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Choose whether this is a live customer organization or a demo for prospect walkthroughs.
      </p>
      <Controller
        name="demoTenant"
        control={control}
        render={({ field }) => (
          <div
            role="radiogroup"
            aria-label="Organization category"
            className="space-y-3"
          >
            <button
              type="button"
              role="radio"
              aria-checked={!demoTenant}
              onClick={() => field.onChange(false)}
              className={cn(
                'w-full flex items-start gap-3 rounded-lg border-2 p-4 text-left transition-all',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                !demoTenant
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'border-border hover:border-primary/40',
              )}
            >
              <Building2
                className={cn(
                  'h-5 w-5 mt-0.5 shrink-0',
                  !demoTenant ? 'text-primary' : 'text-muted-foreground',
                )}
                aria-hidden="true"
              />
              <div>
                <p className={cn(
                  'text-sm font-semibold',
                  !demoTenant ? 'text-primary' : 'text-foreground',
                )}>
                  Live customer org
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Real organization — activates immediately on creation.
                </p>
              </div>
            </button>

            <button
              type="button"
              role="radio"
              aria-checked={demoTenant}
              onClick={() => field.onChange(true)}
              className={cn(
                'w-full flex items-start gap-3 rounded-lg border-2 p-4 text-left transition-all',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                demoTenant
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'border-border hover:border-primary/40',
              )}
            >
              <Sparkles
                className={cn(
                  'h-5 w-5 mt-0.5 shrink-0',
                  demoTenant ? 'text-primary' : 'text-muted-foreground',
                )}
                aria-hidden="true"
              />
              <div>
                <p className={cn(
                  'text-sm font-semibold',
                  demoTenant ? 'text-primary' : 'text-foreground',
                )}>
                  Demo tenant
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Auto-expires in 30 days. Populated with sample data via Seed Demo.
                </p>
              </div>
            </button>
          </div>
        )}
      />
    </div>
  )
}

interface Step5Props {
  values: WizardFormValues
}

const WizardStep5: React.FC<Step5Props> = ({ values }) => (
  <div className="space-y-4">
    <p className="text-sm text-muted-foreground">
      Review your choices before creating the organization.
    </p>
    <div className="rounded-lg border border-border divide-y divide-border overflow-hidden">
      <div className="flex items-center px-4 py-2.5 gap-3">
        <span className="text-xs font-medium text-muted-foreground w-28 shrink-0">Name</span>
        <span className="text-sm text-foreground font-semibold truncate">{values.name}</span>
      </div>
      <div className="flex items-center px-4 py-2.5 gap-3">
        <span className="text-xs font-medium text-muted-foreground w-28 shrink-0">Slug</span>
        <span className="text-sm text-foreground font-mono truncate">/{values.slug}</span>
      </div>
      {values.orgType && (
        <div className="flex items-center px-4 py-2.5 gap-3">
          <span className="text-xs font-medium text-muted-foreground w-28 shrink-0">Type</span>
          <span className="text-sm text-foreground">
            {ORG_TYPE_LABELS[values.orgType] ?? values.orgType}
          </span>
        </div>
      )}
      {values.primaryColor && (
        <div className="flex items-center px-4 py-2.5 gap-3">
          <span className="text-xs font-medium text-muted-foreground w-28 shrink-0">Primary</span>
          <div className="flex items-center gap-2">
            <div
              className="h-4 w-4 rounded-full border border-border shrink-0"
              style={{ backgroundColor: values.primaryColor }}
            />
            <span className="text-sm font-mono text-foreground">{values.primaryColor}</span>
          </div>
        </div>
      )}
      {values.accentColor && (
        <div className="flex items-center px-4 py-2.5 gap-3">
          <span className="text-xs font-medium text-muted-foreground w-28 shrink-0">Accent</span>
          <div className="flex items-center gap-2">
            <div
              className="h-4 w-4 rounded-full border border-border shrink-0"
              style={{ backgroundColor: values.accentColor }}
            />
            <span className="text-sm font-mono text-foreground">{values.accentColor}</span>
          </div>
        </div>
      )}
      {values.website && (
        <div className="flex items-center px-4 py-2.5 gap-3">
          <span className="text-xs font-medium text-muted-foreground w-28 shrink-0">Domain</span>
          <span className="text-sm text-foreground truncate">{values.website}</span>
        </div>
      )}
      <div className="flex items-center px-4 py-2.5 gap-3">
        <span className="text-xs font-medium text-muted-foreground w-28 shrink-0">Logo</span>
        <span className="text-sm text-foreground">
          {values.logoFile ? values.logoFile.name : 'None (add later in Branding tab)'}
        </span>
      </div>
      <div className="flex items-center px-4 py-2.5 gap-3">
        <span className="text-xs font-medium text-muted-foreground w-28 shrink-0">Category</span>
        <span className="text-sm text-foreground">
          {values.demoTenant ? 'Demo tenant (expires in 30 days)' : 'Live customer org'}
        </span>
      </div>
    </div>
  </div>
)

const STEP_FIELDS: Record<WizardStep, (keyof WizardFormValues)[]> = {
  1: ['name', 'slug'],
  2: ['primaryColor', 'accentColor', 'website'],
  3: ['logoFile'],
  4: ['demoTenant'],
  5: [],
}

interface CreateOrgWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (orgId: string) => void
}

const CreateOrgWizard: React.FC<CreateOrgWizardProps> = ({
  open,
  onOpenChange,
  onCreated,
}) => {
  const queryClient = useQueryClient()
  const [step, setStep] = useState<WizardStep>(1)
  const [creating, setCreating] = useState(false)

  const {
    register,
    control,
    watch,
    trigger,
    handleSubmit,
    reset: resetForm,
    setValue,
    formState: { errors },
  } = useForm<WizardFormValues>({
    resolver: zodResolver(wizardSchema),
    defaultValues: {
      name: '',
      slug: '',
      orgType: null,
      primaryColor: '',
      accentColor: '',
      website: '',
      demoTenant: false,
      logoFile: undefined,
    },
    mode: 'onChange',
  })

  const watchedName = watch('name')
  useEffect(() => {
    const generated = watchedName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
    setValue('slug', generated, { shouldDirty: false })
  }, [watchedName, setValue])

  const handleNext = async () => {
    const valid = await trigger(STEP_FIELDS[step])
    if (!valid) return
    setStep((prev) => (prev < 5 ? (prev + 1) as WizardStep : prev))
  }

  const handleBack = () => {
    setStep((prev) => (prev > 1 ? (prev - 1) as WizardStep : prev))
  }

  const onSubmit = async (values: WizardFormValues) => {
    setCreating(true)
    try {
      const created = await adminApi.createOrg({
        name: values.name,
        slug: values.slug,
        orgType: values.orgType as OrgType | undefined ?? undefined,
        demoTenant: values.demoTenant,
        primaryColor: values.primaryColor || undefined,
        accentColor: values.accentColor || undefined,
        website: values.website || undefined,
      })
      if (values.logoFile) {
        try {
          await brandingApi.uploadLogo(created.id, values.logoFile)
        } catch {
          toast.error('Org created but logo upload failed — upload from Branding tab')
        }
      }
      queryClient.invalidateQueries({ queryKey: ['admin-orgs'] })
      toast.success(`"${created.name}" created`)
      onCreated(created.id)
      resetForm()
      setStep(1)
    } catch {
      toast.error('Failed to create organization')
    } finally {
      setCreating(false)
    }
  }

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetForm()
      setStep(1)
    }
    onOpenChange(nextOpen)
  }

  const watchedValues = watch()

  return (
    <Dialog
      open={open}
      onOpenChange={handleClose}
    >
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
          <DialogDescription>
            {step === 1 && 'Start with the basics — name, slug, and type.'}
            {step === 2 && 'Set up branding colors and domain.'}
            {step === 3 && 'Upload a logo (optional).'}
            {step === 4 && 'Is this a live org or a demo?'}
            {step === 5 && 'Review and confirm.'}
          </DialogDescription>
        </DialogHeader>

        <WizardProgress current={step} />

        <form
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && step < 4) {
              e.preventDefault()
              handleNext()
            }
          }}
        >
          {step === 1 && (
            <WizardStep1
              register={register}
              control={control}
              errors={errors}
            />
          )}
          {step === 2 && (
            <WizardStep2 register={register} />
          )}
          {step === 3 && (
            <WizardStep3 control={control} />
          )}
          {step === 4 && (
            <WizardStep4
              control={control}
              watch={watch}
            />
          )}
          {step === 5 && (
            <WizardStep5 values={watchedValues} />
          )}

          <DialogFooter className="mt-6 gap-2">
            {step > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={creating}
              >
                Back
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleClose(false)}
              disabled={creating}
              className="mr-auto"
            >
              Cancel
            </Button>
            {step < 5 ? (
              <Button
                type="button"
                onClick={handleNext}
                disabled={creating}
              >
                {step === 3 && !watchedValues.logoFile ? 'Skip' : 'Next'}
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={creating}
              >
                {creating ? 'Creating…' : 'Create'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

interface OrgCardProps {
  org: AdminOrgFull
  onEdit: (org: AdminOrgFull) => void
}

export const OrgCard: React.FC<OrgCardProps> = ({ org, onEdit }) => {
  const colorClass = deterministicColorClass(org.name)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onEdit(org)
    }
  }
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onEdit(org)}
      onKeyDown={handleKeyDown}
      aria-label={`Edit ${org.name}`}
      className={cn(
        'text-left w-full rounded-lg border border-border bg-card',
        'hover:bg-muted/50 hover:ring-1 hover:ring-border transition-colors duration-150 cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'h-10 w-10 rounded-lg flex items-center justify-center shrink-0 overflow-hidden',
              org.logoUrl ? '' : colorClass,
            )}
          >
            {org.logoUrl ? (
              <img
                src={org.logoUrl}
                alt={`${org.name} logo`}
                className="h-full w-full object-contain"
              />
            ) : (
              <Building className="h-5 w-5" aria-hidden="true" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-foreground truncate">{org.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">/{org.slug}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {org.demoTenant && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                Demo
              </span>
            )}
            {org.orgType && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-secondary text-secondary-foreground">
                {org.orgType}
              </span>
            )}
            <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Actions for ${org.name}`}
                    className="h-8 w-8 text-muted-foreground"
                  >
                    <MoreVertical className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(org)}>
                    Edit
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2.5 pl-[52px]">
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3" aria-hidden="true" />
            {org.memberCount} member{org.memberCount !== 1 ? 's' : ''}
          </span>
          {org.demoTenant && org.expiresAt && (
            <span className="flex items-center gap-1">
              <Building2 className="h-3 w-3" aria-hidden="true" />
              {org.accessCount} visit{org.accessCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

const SkeletonCard: React.FC = () => (
  <div className="rounded-lg border border-border bg-card animate-pulse">
    <div className="p-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-muted shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-3/4 bg-muted rounded" />
          <div className="h-3 w-1/2 bg-muted rounded" />
        </div>
        <div className="h-5 w-16 bg-muted rounded-full" />
      </div>
      <div className="flex gap-4 mt-2.5 pl-[52px]">
        <div className="h-3 w-20 bg-muted rounded" />
      </div>
    </div>
  </div>
)

export const AdminOrgsPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const initialFilter = (searchParams.get('filter') as OrgFilterType) || 'all'
  const [activeFilter, setActiveFilter] = useState<OrgFilterType>(initialFilter)
  const [searchText, setSearchText] = useState(searchParams.get('q') || '')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const { data, isFetching, isError } = useQuery({
    queryKey: ['admin-orgs', activeFilter, searchText],
    queryFn: () => adminApi.listOrgs({
      filter: activeFilter,
      q: searchText || undefined,
      limit: 100,
    }),
    staleTime: 30_000,
  })

  const orgs: AdminOrgFull[] = useMemo(() => data?.orgs ?? [], [data])

  const handleFilterChange = (filter: OrgFilterType) => {
    setActiveFilter(filter)
    const params = new URLSearchParams(searchParams)
    if (filter === 'all') {
      params.delete('filter')
    } else {
      params.set('filter', filter)
    }
    setSearchParams(params, { replace: true })
  }

  const handleSearchChange = (value: string) => {
    setSearchText(value)
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set('q', value)
    } else {
      params.delete('q')
    }
    setSearchParams(params, { replace: true })
  }

  const handleCardEdit = (org: AdminOrgFull) => {
    navigate(`/app/admin/orgs/${org.id}/edit`)
  }

  const handleOrgCreated = (orgId: string) => {
    setCreateDialogOpen(false)
    navigate(`/app/admin/orgs/${orgId}/edit`)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Organizations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage all customer and demo organizations on the platform.
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
          Create org
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Input
            placeholder="Search by name or slug…"
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
            aria-label="Search organizations"
          />
        </div>
        <div
          role="group"
          aria-label="Filter by type"
          className="flex items-center gap-1"
        >
          {(['all', 'customers', 'demos'] as OrgFilterType[]).map((filter) => (
            <button
              key={filter}
              type="button"
              onClick={() => handleFilterChange(filter)}
              aria-pressed={activeFilter === filter}
              aria-label={`Filter: ${FILTER_LABELS[filter]}`}
              className={cn(
                'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border-2 transition-colors',
                activeFilter === filter
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-transparent bg-muted text-muted-foreground hover:bg-muted/80',
              )}
            >
              {FILTER_LABELS[filter]}
            </button>
          ))}
        </div>
        {isFetching && (
          <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
        )}
      </div>

      {isError && (
        <div className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0" aria-hidden="true" />
          <p role="alert" className="text-sm text-destructive flex-1">
            Could not load organizations.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-orgs'] })}
          >
            Try again
          </Button>
        </div>
      )}

      {isFetching && orgs.length === 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </div>
      ) : orgs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
            <Building className="h-12 w-12 text-muted-foreground/50" aria-hidden="true" />
            <p className="text-sm font-medium text-foreground">No organizations yet</p>
            <p className="text-xs text-muted-foreground text-center max-w-xs">
              {searchText
                ? 'Try adjusting your search or filter.'
                : 'Create your first organization'}
            </p>
            {!searchText && (
              <Button
                size="sm"
                onClick={() => setCreateDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                Create org
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {orgs.map((org) => (
            <OrgCard
              key={org.id}
              org={org}
              onEdit={handleCardEdit}
            />
          ))}
        </div>
      )}

      <CreateOrgWizard
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={handleOrgCreated}
      />
    </div>
  )
}
